import Image from 'next/image'
import { tm } from '@captaincause/ui/tm'
import { flattenImage, isLocalProvider } from 'utils/strapi/image'
import { Button } from 'components/Button'
import { StrapiImage } from './StapiImage'
import type {
  LocalImage,
  StrapiApiImage,
  StrapiImage as StrapiImageType,
} from 'types/strapi/image'
import type { StrapiLink } from 'types/strapi/link'

export interface CTABannerProps {
  title: string | null
  description?: string | null
  image: StrapiApiImage | LocalImage
  link: StrapiLink & { locale?: false | undefined }
  color: 'white' | 'purple' | 'beige'
}

const COLORS: Record<CTABannerProps['color'], string> = {
  white: 'bg-white border border-white',
  purple: 'bg-medium-purple border border-light-purple',
  beige: 'bg-medium-beige border border-light-beige',
}

const SHADOWS: Record<CTABannerProps['color'], string> = {
  white:
    'after:shadow-[0px_20px_60px_65px_#FFFFFF] md:after:shadow-[0px_0px_80px_70px_#FFFFFF]',
  purple:
    'after:shadow-[0px_20px_60px_65px_#E0DEFF] md:after:shadow-[0px_0px_80px_70px_#E0DEFF]',
  beige:
    'after:shadow-[0px_0px_50px_65px_#F2F2E4] md:after:shadow-[0px_0px_55px_75px_#F2F2E4]',
}

function CTABannerImage({
  image,
  asBackground,
  color,
}: {
  asBackground: boolean
  color: CTABannerProps['color']
  image: StrapiImageType | LocalImage
}) {
  if (!image) return null

  const isExternalImage = 'url' in image

  if (asBackground) {
    let backgroundUrl
    if (isExternalImage) {
      backgroundUrl = isLocalProvider(image)
        ? `${process.env.NEXT_PUBLIC_STRAPI_MEDIA_API_URL}${image.url}`
        : image.url
    } else {
      backgroundUrl = image.image.src
    }

    return (
      <div
        className={tm(
          "absolute !left-0 !top-0 !h-[50%] !w-[100%] after:absolute after:left-0 after:top-[65%] after:h-full after:w-full after:content-[''] md:!h-[100%] md:!w-[45%] md:max-w-[45%] md:after:left-[97%] md:after:top-0 md:after:w-0",
          SHADOWS[color],
        )}
      >
        <Image
          src={backgroundUrl}
          alt=""
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 500px, 600px"
          className="!left-0 !top-0 !h-[60%] !w-[100%] bg-cover md:!h-[100%] md:!w-[100%]"
        />
      </div>
    )
  }

  if (isExternalImage) {
    return <StrapiImage {...image} className={tm('h-20 w-fit max-w-[80px]')} />
  }

  return (
    <Image
      src={image.image}
      height={72}
      alt={image.alt}
      className="w-fit max-w-[80px]"
    />
  )
}

export function CTABanner({
  title,
  description = null,
  image: propsImage,
  link,
  color: propsColor,
}: CTABannerProps) {
  const image = 'data' in propsImage ? flattenImage(propsImage) : propsImage
  const shouldDisplayImageAsBackground = !title && !description && !!image
  const color = propsColor ?? 'white'

  return (
    <div
      className={tm(
        'relative flex flex-col items-center justify-center space-y-4 overflow-hidden rounded-2xl bg-white px-4 py-4 md:flex-row md:justify-start md:space-x-6 md:space-y-0 md:px-10 lg:space-x-10',
        shouldDisplayImageAsBackground &&
          'space-x-0 space-y-0 pt-[150px] md:space-x-0 md:space-y-0 md:pt-4',
        COLORS[color],
      )}
    >
      <CTABannerImage
        image={image}
        asBackground={shouldDisplayImageAsBackground}
        color={color}
      />
      <div className="flex-1 text-center font-medium md:text-left">
        {title ? <div className="text-2xl">{title}</div> : null}
        {description ? <p className="mt-2">{description}</p> : null}
      </div>
      {link?.title ? (
        <Button
          className={tm(
            'w-full md:w-[unset]',
            color === 'purple' && 'border-purple',
          )}
          variant="secondary"
          href={link.url}
          iconRight="arrow-go"
          locale={link.locale}
        >
          {link.title}
        </Button>
      ) : null}
    </div>
  )
}
